import clsx from 'clsx';
import type { CSSProperties, FunctionComponent, HTMLAttributes, ReactNode } from 'react';

import { recessedSurfaceClassNames } from '../surface/surfaceClasses';

export type RecessedSurfacePlacement = 'inside' | 'outside';
export type RecessedSurfacePocketTone = 'neutral' | 'orange';
export type RecessedSurfaceRadius = 'default' | 'md' | 'xxl';
export type RecessedSurfaceRenderMode = 'layout' | 'overlay';
export type RecessedSurfaceShadow = 'none' | 'orange-sm' | 'sm';
export type RecessedSurfaceTone = 'gray' | 'inherit' | 'orange' | 'white';
export type RecessedSurfaceTransition = 'default' | 'none';

const pocketClassNameByTone: Record<RecessedSurfacePocketTone, string> = {
    neutral: recessedSurfaceClassNames.pocket.neutral,
    orange: recessedSurfaceClassNames.pocket.orange,
};

const hoverPocketClassNameByTone: Record<RecessedSurfacePocketTone, string> = {
    neutral: 'hover:surface-recessed-pocket-neutral',
    orange: 'hover:surface-recessed-pocket-orange',
};

const groupHoverAndFocusPocketClassNameByTone: Record<RecessedSurfacePocketTone, string> = {
    neutral:
        'group-hover/recessed-surface:surface-recessed-pocket-neutral group-focus-within/recessed-surface:surface-recessed-pocket-neutral',
    orange: 'group-hover/recessed-surface:surface-recessed-pocket-orange group-focus-within/recessed-surface:surface-recessed-pocket-orange',
};

const surfaceTextClassNameByTone: Record<RecessedSurfaceTone, string> = {
    gray: 'text-text-content-subtle',
    inherit: '',
    orange: 'text-text-on-accent',
    white: 'text-text-content-subtle',
};

const hoverAndFocusTextClassNameByTone: Record<RecessedSurfaceTone, string> = {
    gray: 'hover:text-text-content-subtle focus-within:text-text-content-subtle',
    inherit: '',
    orange: 'hover:text-text-on-accent focus-within:text-text-on-accent',
    white: 'hover:text-text-content-subtle focus-within:text-text-content-subtle',
};

const surfaceClassNameByTone: Record<RecessedSurfaceTone, string> = {
    gray: recessedSurfaceClassNames.inner.gray,
    inherit: '',
    orange: recessedSurfaceClassNames.inner.orange,
    white: 'bg-surface-raised text-text-content-subtle',
};

const radiusClassNamesByRadius: Record<RecessedSurfaceRadius, { readonly inner: string; readonly pocket: string }> = {
    default: {
        inner: recessedSurfaceClassNames.radius.inner,
        pocket: recessedSurfaceClassNames.radius.pocket,
    },
    md: {
        inner: recessedSurfaceClassNames.radius.innerMd,
        pocket: recessedSurfaceClassNames.radius.pocketMd,
    },
    xxl: {
        inner: recessedSurfaceClassNames.radius.innerXxl,
        pocket: recessedSurfaceClassNames.radius.pocketXxl,
    },
};

const surfaceShadowByName: Record<RecessedSurfaceShadow, string | undefined> = {
    none: undefined,
    'orange-sm': 'var(--shadow-orange-sm)',
    sm: 'var(--shadow-sm)',
};

const hoverSurfaceClassNameByTone: Record<RecessedSurfaceTone, string> = {
    gray: 'group-hover/recessed-surface:surface-recessed-inset-gray',
    inherit: '',
    orange: 'group-hover/recessed-surface:surface-recessed-inset-orange',
    white: 'group-hover/recessed-surface:bg-surface-raised group-hover/recessed-surface:text-text-content-subtle',
};

const hoverAndFocusSurfaceClassNameByTone: Record<RecessedSurfaceTone, string> = {
    gray: 'group-hover/recessed-surface:surface-recessed-inset-gray group-focus-within/recessed-surface:surface-recessed-inset-gray',
    inherit: '',
    orange: 'group-hover/recessed-surface:surface-recessed-inset-orange group-focus-within/recessed-surface:surface-recessed-inset-orange',
    white: 'group-hover/recessed-surface:bg-surface-raised group-hover/recessed-surface:text-text-content-subtle group-focus-within/recessed-surface:bg-surface-raised group-focus-within/recessed-surface:text-text-content-subtle',
};

const hoverSurfaceShadowClassNameByName: Record<RecessedSurfaceShadow, string | undefined> = {
    none: undefined,
    'orange-sm': 'group-hover/recessed-surface:[box-shadow:var(--shadow-orange-sm)]',
    sm: 'group-hover/recessed-surface:[box-shadow:var(--shadow-sm)]',
};

const hoverAndFocusSurfaceShadowClassNameByName: Record<RecessedSurfaceShadow, string | undefined> = {
    none: undefined,
    'orange-sm':
        'group-hover/recessed-surface:[box-shadow:var(--shadow-orange-sm)] group-focus-within/recessed-surface:[box-shadow:var(--shadow-orange-sm)]',
    sm: 'group-hover/recessed-surface:[box-shadow:var(--shadow-sm)] group-focus-within/recessed-surface:[box-shadow:var(--shadow-sm)]',
};

const getPocketLayoutClassName = ({
    radius,
    shouldPreserveInactiveStructure,
    shouldRenderInactiveSurfaceShell,
}: {
    readonly radius: RecessedSurfaceRadius;
    readonly shouldPreserveInactiveStructure: boolean;
    readonly shouldRenderInactiveSurfaceShell: boolean;
}): string =>
    shouldPreserveInactiveStructure && !shouldRenderInactiveSurfaceShell
        ? 'overflow-visible rounded-none p-0'
        : clsx('overflow-hidden p-xxs', radiusClassNamesByRadius[radius].pocket);

const getInnerLayoutClassName = ({
    radius,
    shouldPreserveInactiveStructure,
    shouldRenderInactiveSurfaceShell,
}: {
    readonly radius: RecessedSurfaceRadius;
    readonly shouldPreserveInactiveStructure: boolean;
    readonly shouldRenderInactiveSurfaceShell: boolean;
}): string =>
    shouldPreserveInactiveStructure && !shouldRenderInactiveSurfaceShell
        ? 'overflow-visible rounded-none'
        : clsx('overflow-hidden', radiusClassNamesByRadius[radius].inner);

type RecessedSurfaceLayersProps = {
    readonly active: boolean;
    readonly as?: RecessedSurfaceComponent;
    readonly hoverActive?: boolean;
    readonly hoverPocketTone?: RecessedSurfacePocketTone;
    readonly hoverSurfaceShadow?: RecessedSurfaceShadow;
    readonly hoverSurfaceTone?: RecessedSurfaceTone;
    readonly inactiveSurfaceTone?: RecessedSurfaceTone;
    readonly pocketClassName?: string;
    readonly pocketStyle?: CSSProperties;
    readonly pocketTone?: RecessedSurfacePocketTone;
    readonly radius?: RecessedSurfaceRadius;
    readonly surfaceClassName?: string;
    readonly surfaceShadow?: RecessedSurfaceShadow;
    readonly surfaceStyle?: CSSProperties;
    readonly surfaceTone?: RecessedSurfaceTone;
    readonly transition?: RecessedSurfaceTransition;
};

export const RecessedSurfaceLayers: FunctionComponent<RecessedSurfaceLayersProps> = ({
    active,
    as: Component = 'div',
    hoverActive = false,
    hoverPocketTone = 'neutral',
    hoverSurfaceShadow = 'none',
    hoverSurfaceTone = 'inherit',
    inactiveSurfaceTone = 'inherit',
    pocketClassName,
    pocketStyle,
    pocketTone = 'neutral',
    radius = 'default',
    surfaceClassName,
    surfaceShadow = 'none',
    surfaceStyle,
    surfaceTone = 'inherit',
    transition = 'default',
}) => {
    const activeSurfaceShadow = active ? surfaceShadowByName[surfaceShadow] : undefined;
    const mergedSurfaceStyle: CSSProperties | undefined =
        activeSurfaceShadow === undefined ? surfaceStyle : { boxShadow: activeSurfaceShadow, ...surfaceStyle };

    return (
        <>
            <Component
                aria-hidden={true}
                className={clsx(
                    'pointer-events-none absolute inset-0 transition-[background-color,box-shadow] duration-200 ease',
                    radiusClassNamesByRadius[radius].pocket,
                    transition === 'none' && 'transition-none',
                    active
                        ? pocketClassNameByTone[pocketTone]
                        : hoverActive && groupHoverAndFocusPocketClassNameByTone[hoverPocketTone],
                    pocketClassName,
                )}
                style={pocketStyle}
            />
            <Component
                aria-hidden={true}
                className={clsx(
                    'pointer-events-none absolute transition-[inset,background-color,box-shadow] duration-200 ease',
                    radiusClassNamesByRadius[radius].inner,
                    transition === 'none' && 'transition-none',
                    surfaceClassNameByTone[active ? surfaceTone : inactiveSurfaceTone],
                    active
                        ? 'inset-xxs'
                        : clsx(
                              'inset-0',
                              hoverActive &&
                                  'group-hover/recessed-surface:inset-xxs group-focus-within/recessed-surface:inset-xxs',
                          ),
                    hoverActive && !active && hoverAndFocusSurfaceClassNameByTone[hoverSurfaceTone],
                    hoverActive && !active && hoverAndFocusSurfaceShadowClassNameByName[hoverSurfaceShadow],
                    surfaceClassName,
                )}
                style={mergedSurfaceStyle}
            />
        </>
    );
};

type RecessedSurfaceProps = HTMLAttributes<HTMLElement> & {
    readonly active: boolean;
    readonly as?: 'div' | 'span';
    readonly children: ReactNode;
    readonly hoverActive?: boolean;
    readonly hoverPocketTone?: RecessedSurfacePocketTone;
    readonly hoverSurfaceShadow?: RecessedSurfaceShadow;
    readonly hoverSurfaceTone?: RecessedSurfaceTone;
    readonly inactiveBehavior?: 'passthrough' | 'preserve-structure';
    readonly inactiveSurfaceShadow?: RecessedSurfaceShadow;
    readonly inactiveSurfaceTone?: RecessedSurfaceTone;
    readonly placement?: RecessedSurfacePlacement;
    readonly pocketTone?: RecessedSurfacePocketTone;
    readonly radius?: RecessedSurfaceRadius;
    readonly renderMode?: RecessedSurfaceRenderMode;
    readonly surfaceClassName?: string;
    readonly surfaceLayerClassName?: string;
    readonly surfaceShadow?: RecessedSurfaceShadow;
    readonly surfaceStyle?: CSSProperties;
    readonly surfaceTone?: RecessedSurfaceTone;
    readonly transition?: RecessedSurfaceTransition;
};

type RecessedSurfaceComponent = 'div' | 'span';

type RecessedSurfaceOverlayProps = HTMLAttributes<HTMLElement> & {
    readonly active: boolean;
    readonly children: ReactNode;
    readonly Component: RecessedSurfaceComponent;
    readonly hoverActive: boolean;
    readonly hoverPocketTone: RecessedSurfacePocketTone;
    readonly hoverSurfaceShadow: RecessedSurfaceShadow;
    readonly hoverSurfaceTone: RecessedSurfaceTone;
    readonly inactiveSurfaceTone: RecessedSurfaceTone;
    readonly pocketTone: RecessedSurfacePocketTone;
    readonly radius: RecessedSurfaceRadius;
    readonly selectedSurfaceStyle: CSSProperties | undefined;
    readonly surfaceClassName: string | undefined;
    readonly surfaceLayerClassName: string | undefined;
    readonly surfaceTone: RecessedSurfaceTone;
    readonly transition: RecessedSurfaceTransition;
};

const RecessedSurfaceOverlay: FunctionComponent<RecessedSurfaceOverlayProps> = ({
    active,
    children,
    className,
    Component,
    hoverActive,
    hoverPocketTone,
    hoverSurfaceShadow,
    hoverSurfaceTone,
    inactiveSurfaceTone,
    pocketTone,
    radius,
    selectedSurfaceStyle,
    style,
    surfaceClassName,
    surfaceLayerClassName,
    surfaceTone,
    transition,
    ...props
}) => (
    <Component
        className={clsx(
            'relative box-border w-full',
            hoverActive && 'group/recessed-surface',
            active
                ? surfaceTextClassNameByTone[surfaceTone]
                : clsx(
                      surfaceTextClassNameByTone[inactiveSurfaceTone],
                      hoverActive && hoverAndFocusTextClassNameByTone[hoverSurfaceTone],
                  ),
            Component === 'span' && 'inline-flex',
            className,
        )}
        style={style}
        {...props}
    >
        <RecessedSurfaceLayers
            active={active}
            as={Component}
            hoverActive={hoverActive}
            hoverPocketTone={hoverPocketTone}
            hoverSurfaceShadow={hoverSurfaceShadow}
            hoverSurfaceTone={hoverSurfaceTone}
            inactiveSurfaceTone={inactiveSurfaceTone}
            pocketTone={pocketTone}
            radius={radius}
            surfaceClassName={surfaceLayerClassName}
            surfaceStyle={selectedSurfaceStyle}
            surfaceTone={surfaceTone}
            transition={transition}
        />
        <Component
            className={clsx(
                'relative box-border h-full w-full',
                Component === 'span' && 'inline-flex',
                surfaceClassName,
            )}
        >
            {children}
        </Component>
    </Component>
);

export const RecessedSurface: FunctionComponent<RecessedSurfaceProps> = ({
    active,
    as: Component = 'div',
    children,
    className,
    hoverActive = false,
    hoverPocketTone = 'neutral',
    hoverSurfaceShadow = 'none',
    hoverSurfaceTone = 'inherit',
    inactiveBehavior = 'passthrough',
    inactiveSurfaceShadow = 'none',
    inactiveSurfaceTone = 'inherit',
    placement = 'outside',
    pocketTone = 'neutral',
    radius = 'default',
    renderMode = 'layout',
    style,
    surfaceClassName,
    surfaceLayerClassName,
    surfaceShadow = 'none',
    surfaceStyle,
    surfaceTone = 'inherit',
    transition = 'default',
    ...props
}) => {
    const shouldPreserveInactiveStructure =
        !active && placement === 'inside' && inactiveBehavior === 'preserve-structure';
    const shouldRenderInactiveSurfaceShell = shouldPreserveInactiveStructure && hoverActive;

    if (!active && placement === 'inside' && !shouldPreserveInactiveStructure) {
        return <>{children}</>;
    }

    const pocketStyle: CSSProperties = style ?? {};
    const selectedSurfaceShadow = surfaceShadowByName[active ? surfaceShadow : inactiveSurfaceShadow];
    const selectedSurfaceStyle: CSSProperties | undefined =
        selectedSurfaceShadow === undefined ? surfaceStyle : { boxShadow: selectedSurfaceShadow, ...surfaceStyle };

    if (renderMode === 'overlay') {
        return (
            <RecessedSurfaceOverlay
                active={active}
                className={className}
                Component={Component}
                hoverActive={hoverActive}
                hoverPocketTone={hoverPocketTone}
                hoverSurfaceShadow={hoverSurfaceShadow}
                hoverSurfaceTone={hoverSurfaceTone}
                inactiveSurfaceTone={inactiveSurfaceTone}
                pocketTone={pocketTone}
                radius={radius}
                selectedSurfaceStyle={selectedSurfaceStyle}
                style={style}
                surfaceClassName={surfaceClassName}
                surfaceLayerClassName={surfaceLayerClassName}
                surfaceTone={surfaceTone}
                transition={transition}
                {...props}
            >
                {children}
            </RecessedSurfaceOverlay>
        );
    }

    return (
        <Component
            className={clsx(
                'box-border w-full transition-[background-color,box-shadow] duration-200 ease',
                transition === 'none' && 'transition-none',
                hoverActive && 'group/recessed-surface',
                getPocketLayoutClassName({ radius, shouldPreserveInactiveStructure, shouldRenderInactiveSurfaceShell }),
                Component === 'span' && 'inline-flex',
                active ? pocketClassNameByTone[pocketTone] : 'bg-surface-subtle',
                shouldPreserveInactiveStructure && 'bg-transparent',
                hoverActive && !active && hoverPocketClassNameByTone[hoverPocketTone],
                placement === 'inside' && 'h-full',
                className,
            )}
            style={pocketStyle}
            {...props}
        >
            <Component
                className={clsx(
                    'box-border w-full transition-[background-color,box-shadow] duration-200 ease',
                    transition === 'none' && 'transition-none',
                    getInnerLayoutClassName({
                        radius,
                        shouldPreserveInactiveStructure,
                        shouldRenderInactiveSurfaceShell,
                    }),
                    Component === 'span' && 'inline-flex',
                    surfaceClassNameByTone[active ? surfaceTone : inactiveSurfaceTone],
                    hoverActive && !active && hoverSurfaceClassNameByTone[hoverSurfaceTone],
                    hoverActive && !active && hoverSurfaceShadowClassNameByName[hoverSurfaceShadow],
                    placement === 'inside' && 'h-full',
                    (!shouldPreserveInactiveStructure || shouldRenderInactiveSurfaceShell) &&
                        clsx(surfaceClassName, surfaceLayerClassName),
                )}
                style={selectedSurfaceStyle}
            >
                {children}
            </Component>
        </Component>
    );
};
